import { useRef } from 'react';
import type {
	Deployment,
	DeploymentEnvironment as Environment,
	EnvironmentName,
} from '../../../../common/types';
import { useAllDeployments } from '../../../../services/core-data/main.tsx';

/**
 * This hook returns all environments we've encountered so far, including those from
 * previous filter configurations. If we didn't do this, environments would disappear
 * from the filter dropdown when a new filter is applied!
 *
 * "filter deployments by environment" API only accepts
 * names, backed my JQL. From a UI perspective, this means API will consider environments with
 * the same name as being identical, although if environments with the same name but differnet type will be displayed separately.
 * Not ideal, but it's the best we can do with the current API.
 */
export const useAllEnvironments = (): Environment[] => {
	type EnvironmentsByName = Map<EnvironmentName, Environment>;
	const { current: allKnownEnvironments } = useRef<EnvironmentsByName>(new Map());
	const { data: deploymentsForCurrentFilters = [] } = useAllDeployments();

	deploymentsForCurrentFilters.forEach(({ environment }: Deployment) => {
		// Environment name could be same but type could be different. For customer example, https://getsupport.atlassian.com/browse/PCS-178628
		allKnownEnvironments.set(`${environment.type}-${environment.displayName}`, environment);
	});

	return [...allKnownEnvironments.values()];
};
